import React, {
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ChevronDown, Search, Tag as TagIcon, X } from 'lucide-react';
import { ClipLoader } from 'react-spinners';
import { useAssignableTags } from 'service/crm';
import {
  CrmAssignableTag,
  CrmTagTarget,
  normalizeCrmTagColor,
  tagChipStyle,
} from 'types/crm/tag';

interface TagSelectProps {
  /** Tag names, which is what the Contact/Company request body takes. */
  value: string[];
  onChange: (tags: string[]) => void;
  /** Decides which tags are on offer: a tag may apply to one type or both. */
  recordType: CrmTagTarget;
  /**
   * The record's existing tags, when editing. Only used for their colour: a
   * tag made Inactive since is gone from the assignable list, so this is the
   * one place its colour can still come from.
   */
  currentTags?: { name: string; colorKey?: string }[];
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

/** Every row is the same height, which is what lets the list skip measuring. */
const ROW_HEIGHT = 36;

/**
 * The scrolling list of tags, drawing only the rows in view.
 *
 * An organisation can have thousands of tags, and the `assignable` endpoint
 * returns them all at once. Rendering a button for each froze the page for
 * seconds every time the panel opened; virtualised, it draws the dozen or so
 * rows that fit, plus a few either side, however long the list is.
 *
 * Its own component so it mounts with the panel: the virtualiser needs the
 * scroll container to exist, and that only happens once the panel is open.
 */
const TagOptionList: React.FC<{
  options: CrmAssignableTag[];
  onPick: (name: string) => void;
  /** Changes when the search does, so the list returns to the top of the new results. */
  resetKey: string;
}> = ({ options, onPick, resetKey }) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: options.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 8,
  });

  // A new search starts from its first match, not wherever the last one was
  // scrolled to. Picking a tag does not reset it, so several can be picked
  // from the same stretch of the list.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [resetKey]);

  return (
    <div ref={scrollRef} className="custom-scrollbar max-h-60 overflow-y-auto">
      <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
        {virtualizer.getVirtualItems().map((item) => {
          const tag = options[item.index];

          return (
            <button
              key={tag.uuid}
              type="button"
              onClick={() => onPick(tag.name)}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: item.size,
                transform: `translateY(${item.start}px)`,
              }}
              className="flex items-center gap-2.5 px-3 text-left text-sm text-neutral transition-colors hover:bg-primary-subtle focus:bg-primary-subtle focus:outline-none"
            >
              <span
                aria-hidden="true"
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: normalizeCrmTagColor(tag.colorKey) }}
              />
              <span className="truncate">{tag.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Picks the tags on a Contact or Company, from the organisation's tag library.
 *
 * Deliberately not a free-text field. Tags are created in Settings → Tags and
 * nowhere else — the API rejects a name that is not in the library — so
 * offering a box to type into would only invite a save that fails. The library
 * is the list; this picks from it.
 */
const TagSelect: React.FC<TagSelectProps> = ({
  value,
  onChange,
  recordType,
  currentTags = [],
  disabled = false,
  placeholder = 'Add tags',
  className = '',
}) => {
  const [query, setQuery] = useState('');
  // Filtering thousands of names on every keystroke would make the box lag;
  // deferred, the typing stays immediate and the list catches up a beat later.
  const deferredQuery = useDeferredValue(query);

  const { data, isLoading, isError, error } = useAssignableTags(recordType);

  // A tag with no name cannot be picked or searched, and would throw on
  // `toLowerCase` — dropped here so one bad row cannot take the field down.
  const assignableTags = useMemo(
    () => (data ?? []).filter((tag) => typeof tag?.name === 'string' && tag.name),
    [data],
  );

  /** What the server said, when it would not hand over the list. */
  const loadError = isError
    ? (error as { message?: string } | null)?.message || 'Could not load tags.'
    : null;

  /**
   * Each chip's colour, by tag name. Assignable wins over the record's own
   * copy — it is the live library, so a tag recoloured since the record was
   * saved shows its new colour.
   */
  const colorByName = useMemo(() => {
    const map = new Map<string, string | undefined>();
    currentTags.forEach((tag) => map.set(tag.name, tag.colorKey));
    assignableTags.forEach((tag) => map.set(tag.name, tag.colorKey));
    return map;
  }, [assignableTags, currentTags]);

  /** A name no longer on offer — made Inactive, or narrowed to the other record type. */
  const assignableNames = useMemo(
    () => new Set(assignableTags.map((tag) => tag.name)),
    [assignableTags],
  );

  const options = useMemo(() => {
    const selected = new Set(value);
    const term = deferredQuery.trim().toLowerCase();

    return assignableTags.filter(
      (tag) =>
        !selected.has(tag.name) &&
        (!term || tag.name.toLowerCase().includes(term)),
    );
  }, [assignableTags, value, deferredQuery]);

  const addTag = (name: string) => {
    if (!value.includes(name)) onChange([...value, name]);
    // The panel stays open so several tags can be added in a row; only the
    // search is reset, so the next pick starts from the full list.
    setQuery('');
  };

  const removeTag = (name: string) => onChange(value.filter((t) => t !== name));

  const emptyMessage = () => {
    // Checked before the "no tags" case: a failed request also leaves the list
    // empty, and telling someone to go create tags when the real problem is a
    // refused request sends them the wrong way.
    if (loadError) return `Couldn't load tags: ${loadError}`;
    if (assignableTags.length === 0) {
      return 'No tags available. Add them in Settings → Tags.';
    }
    if (query.trim()) return `No tags match "${query.trim()}".`;
    return 'Every available tag is already added.';
  };

  return (
    <div className={`w-full ${className}`}>
      {value.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {value.map((name) => {
            // Still on the record, no longer offered. Shown muted rather than
            // hidden — removing it is allowed, re-adding it is not.
            const isRetired = !isLoading && !assignableNames.has(name);

            return (
              <span
                key={name}
                style={tagChipStyle(colorByName.get(name))}
                title={
                  isRetired
                    ? `"${name}" is no longer available for new records`
                    : undefined
                }
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${
                  isRetired ? 'opacity-60' : ''
                }`}
              >
                {name}
                {!disabled && (
                  <button
                    type="button"
                    aria-label={`Remove ${name}`}
                    onClick={() => removeTag(name)}
                    className="rounded-full p-0.5 transition-colors hover:bg-black/25"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}

      <Popover className="relative">
        <PopoverButton
          disabled={disabled}
          className="flex h-10 w-full items-center gap-2 rounded-lg border border-primary-subtle bg-primary-light px-3 text-left text-sm text-neutral-medium transition-colors hover:border-neutral-medium/50 focus:outline-none data-[open]:border-[#8B5CF6] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isLoading ? (
            <ClipLoader size={14} color="#8B5CF6" />
          ) : (
            <TagIcon className="h-4 w-4 shrink-0" />
          )}
          <span className="flex-1 truncate">
            {isLoading ? 'Loading tags...' : placeholder}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0" />
        </PopoverButton>

        {/* `focus` moves focus into the panel, which lands on the search box. */}
        <PopoverPanel
          focus
          anchor="bottom start"
          className="z-[9999] mt-2 w-[var(--button-width)] min-w-[240px] overflow-hidden rounded-lg border border-primary-subtle bg-primary-variant shadow-xl focus:outline-none"
        >
          <div className="border-b border-primary-subtle p-2">
            <div className="flex items-center gap-2 rounded-md bg-primary-light px-2.5">
              <Search className="h-3.5 w-3.5 shrink-0 text-neutral-medium" />
              <input
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  // Enter must never reach the surrounding form — it would
                  // submit the whole contact. Here it picks the first match.
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    if (options[0]) addTag(options[0].name);
                  }
                }}
                placeholder="Search tags..."
                autoComplete="off"
                className="h-9 w-full min-w-0 bg-transparent text-sm text-neutral placeholder:text-neutral-medium focus:outline-none"
              />
            </div>
          </div>

          <div className="py-1.5">
            {isLoading ? (
              <div className="flex items-center justify-center gap-2 px-3 py-6 text-sm text-neutral-medium">
                <ClipLoader size={16} color="#8B5CF6" />
                Loading tags...
              </div>
            ) : options.length === 0 ? (
              <div className="px-3 py-2 text-sm text-neutral-medium">
                {emptyMessage()}
              </div>
            ) : (
              <TagOptionList
                options={options}
                onPick={addTag}
                resetKey={deferredQuery}
              />
            )}
          </div>
        </PopoverPanel>
      </Popover>

      {/* On the page as well as in the panel, so a refused request is seen
          without having to open the field to find out. */}
      {loadError && (
        <p role="alert" className="mt-1.5 text-xs text-red-300">
          Couldn't load tags: {loadError}
        </p>
      )}
    </div>
  );
};

export default TagSelect;
